import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import {
  FiChevronRight,
  FiHome,
  FiFile,
  FiImage,
  FiFilm,
  FiMusic,
  FiFolder,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import TinyContextMenu from "../../components/tiny-context-menu/TinyContextMenu";
import UploadOverlay from "../../components/upload/UploadOverlay";
import uploadFile from "../../utils/uploadFile";
import { openFile, deleteFile, moveFile } from "../../utils/storageFiles";
import { getPermissions, getOwnerUid } from "../../utils/permissions";
import "./Files.scss";

// Account allowance in bytes; edit these values in both apps together.
const STORAGE_LIMITS = { free: 50 * 1024 ** 2, antPro: 1024 ** 3, antBusiness: 5 * 1024 ** 3 };

const groups = [
  ["project", "Project Files"],
  ["workspace", "Workspace Files"],
  ["photos", "Photos from Tasks & Notes"],
];
const previewKind = (type = "") =>
  /^(image\/(jpeg|png|gif|webp)|video\/(mp4|webm)|audio\/(mpeg|mp4|ogg|wav))$/.test(type)
    ? type.split("/")[0]
    : type === "application/pdf"
      ? "pdf"
      : null;
const fileIcon = (type = "") =>
  type.startsWith("image/")
    ? FiImage
    : type.startsWith("video/")
      ? FiFilm
      : type.startsWith("audio/")
        ? FiMusic
        : FiFile;
const sizeLabel = (n = 0) => (n < 1048576 ? `${Math.ceil(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`);

export default function Files({ station, workspace, billingAccount = null }) {
  const currentUser = useSelector((s) => s.user.currentUser);
  const uid = currentUser?.uid;
  const entitlement = useSelector((s) => s.user.entitlement);
  const state = useSelector((s) => s.files);
  const spaceId = station?.fromSpaceId || station?.spaceId;
  const stationId = station?.stationId || station?.id;
  const key = JSON.stringify([uid, spaceId, stationId]);
  const permissions = getPermissions(
    workspace && { ...workspace, spaceId },
    { ...station, fromSpaceId: spaceId },
    uid,
  );
  const [expanded, setExpanded] = useState({ project: true, workspace: false, photos: false });
  const [folder, setFolder] = useState(null);
  const [search, setSearch] = useState("");
  const [upload, setUpload] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [viewer, setViewer] = useState(null);
  const [moving, setMoving] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleted, setDeleted] = useState({}); // Hide confirmed deletions while the listener catches up.
  const lock = useRef(false);
  const opening = useRef(0);
  const files = useMemo(
    () =>
      state?.key === key && permissions.canViewProject
        ? [...state.project, ...(permissions.canViewWorkspace ? state.workspace : [])].filter(
            (file) => !deleted[`${key}:${file.id}`],
          )
        : [],
    [state, key, deleted, permissions.canViewWorkspace, permissions.canViewProject],
  );
  const folders = [
    ...new Set(files.filter((f) => f.group === "project" && f.folder).map((f) => f.folder)),
  ].sort();
  useEffect(
    () => () => {
      opening.current++;
    },
    [key],
  );
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 3500);
    return () => clearTimeout(timer);
  }, [notice]);

  // WIRE HERE: pass the cached workspace owner's account for member uploads.
  // Missing owner data skips this optional UI check; it never uses a member's plan.
  const ownerUid = getOwnerUid(workspace);
  const allowanceAccount =
    ownerUid === uid
      ? { ...currentUser, entitlement }
      : billingAccount?.uid === ownerUid
        ? billingAccount
        : null;
  const checkStorageAllowance = (bytes) => {
    const account = allowanceAccount;
    const limit = STORAGE_LIMITS[account?.entitlement];
    if (!account || !Number.isFinite(account.storageUsed) || !Number.isFinite(limit)) return true;
    const remaining = Math.max(0, limit + (account.storageExtra || 0) - account.storageUsed);
    if (bytes <= remaining) return true;
    window.alert(`Not enough workspace storage. Only ${sizeLabel(remaining)} available.`);
    return false;
  };

  const closeViewer = () => {
    opening.current++;
    setViewer(null);
  };

  const pick = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (lock.current || !selected.length || !permissions.canEditProjectContent) return;
    if (!checkStorageAllowance(selected.reduce((total, file) => total + file.size, 0))) return;
    lock.current = true;
    setError("");
    setNotice("");
    let failed = 0;
    try {
      for (let i = 0; i < selected.length; i++) {
        const file = selected[i];
        const name = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const id = window.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(16).slice(2)}`;
        setUpload({ progress: 0, status: `Uploading file ${i + 1} of ${selected.length}` });
        try {
          await uploadFile({
            file,
            userId: uid,
            fullPath: `space/${spaceId}/stations/${stationId}/files/${id}_${name}`,
            onProgress: ({ progress }) =>
              setUpload({
                progress: progress * 100,
                status: progress === 1 ? "Finishing upload" : `Uploading file ${i + 1} of ${selected.length}`,
              }),
          });
        } catch (e) {
          failed++;
          setError(e.message || "Upload failed.");
        }
      }
      setNotice(`${selected.length - failed} file(s) uploaded to Main. They may take a moment to appear.`);
      setFolder(null);
    } finally {
      lock.current = false;
      setUpload(null);
    }
  };
  useEffect(
    () => () => {
      if (viewer?.url?.startsWith("blob:")) URL.revokeObjectURL(viewer.url);
    },
    [viewer?.url],
  );
  const open = async (file) => {
    const request = ++opening.current;
    setViewer({ file, loading: true });
    try {
      const url = await openFile(file);
      if (previewKind(file.type) === "pdf") {
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error("PDF unavailable");
          const blob = await response.blob();
          if (!(await blob.slice(0, 1024).text()).includes("%PDF-")) throw new Error("Invalid PDF");
          if (request !== opening.current) return;
          setViewer({
            file,
            downloadUrl: url,
            url: URL.createObjectURL(new Blob([blob], { type: "application/pdf" })),
          });
        } catch (e) {
          if (request === opening.current) setViewer({ file, url, previewFailed: true });
        }
      } else if (request === opening.current) setViewer({ file, url });
    } catch (e) {
      if (request === opening.current)
        setViewer({ file, error: "This file could not be opened. Please try again." });
    }
  };
  const remove = async (file) => {
    if (
      lock.current ||
      !window.confirm(
        `Delete “${file.name}”? This also removes the stored file. Existing links to it will stop working.`,
      )
    )
      return;
    lock.current = true;
    setBusy(true);
    setDeleting(file.id);
    setNotice("");
    setError("");
    try {
      await deleteFile(file);
      setDeleted((old) => ({ ...old, [`${key}:${file.id}`]: true }));
      setNotice("File deleted.");
    } catch (e) {
      setError(
        e.code === "storage/object-not-found"
          ? "This file is no longer available. Reopen the project to refresh the list."
          : "Could not delete this file.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
      setDeleting(null);
    }
  };
  const saveFolder = async (event) => {
    event.preventDefault();
    const value = folderName.trim() || null;
    if (lock.current) return;
    if (value && (value.length > 80 || /[/\\]/.test(value))) {
      setError("Use a folder name of up to 80 characters without slashes.");
      return;
    }
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      await moveFile(moving, value);
      setMoving(null);
    } catch (e) {
      setError("Could not move this file.");
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return (
    <div className="project-files">
      <div className="project-files__tools">
        <input
          type="search"
          aria-label="Search file names"
          placeholder="Search files…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {groups
        .filter(([id]) => id !== "workspace" || permissions.canViewWorkspace)
        .map(([id, title]) => {
          const items = files
            .filter(
              (f) =>
                f.group === id &&
                (id !== "project" || (f.folder || null) === folder) &&
                f.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
            )
            .sort((a, b) => a.name.localeCompare(b.name));
          const visibleFolders =
            id === "project" && !folder
              ? folders.filter(
                  (name) =>
                    name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
                    files.some(
                      (f) =>
                        f.group === "project" &&
                        f.folder === name &&
                        f.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
                    ),
                )
              : [];
          const scope = id === "workspace" ? "workspace" : "project";
          const loading = state?.key !== key || state?.[`${scope}Loading`];
          const loadError = state?.key === key && state?.[`${scope}Error`];
          const editable =
            id === "workspace" ? permissions.canEditWorkspaceBrief : permissions.canEditProjectContent;
          return (
            <section key={id}>
              <button
                type="button"
                className="project-files__heading"
                aria-expanded={expanded[id]}
                aria-controls={`files-${id}`}
                onClick={() => setExpanded((old) => ({ ...old, [id]: !old[id] }))}>
                <span>{title}</span>
                <FiChevronRight className={expanded[id] ? "is-open" : ""} />
              </button>
              {expanded[id] && (
                <div id={`files-${id}`} className="project-files__body">
                  {id === "project" && (
                    <div className="project-files__tools">
                      <nav className="project-files__breadcrumbs" aria-label="Folder location">
                        <button
                          type="button"
                          aria-label="All project folders"
                          title="Project files"
                          onClick={() => setFolder(null)}>
                          <FiHome />
                        </button>
                        {folder && (
                          <>
                            <FiChevronRight />
                            <span aria-current="page">{folder}</span>
                          </>
                        )}
                      </nav>
                      {editable &&
                        [false, true].map((media) => (
                          <label key={String(media)} className="project-files__upload">
                            {media ? "Photos & videos" : "Upload files"}
                            <input
                              type="file"
                              multiple
                              accept={media ? "image/*,video/*" : undefined}
                              disabled={!!upload || busy}
                              onChange={pick}
                            />
                          </label>
                        ))}
                    </div>
                  )}
                  {loading ? (
                    <p role="status">Loading files…</p>
                  ) : loadError ? (
                    <p role="alert">{loadError}</p>
                  ) : !items.length && !visibleFolders.length ? (
                    <p>{search || folder ? "No matching files." : "No files here yet."}</p>
                  ) : (
                    <div className="project-files__grid">
                      {visibleFolders.map((name) => (
                        <button
                          key={name}
                          type="button"
                          className="project-files__folder"
                          onClick={() => setFolder(name)}
                          title={name}>
                          <FiFolder size={24} />
                          <span>{name}</span>
                          <FiChevronRight />
                        </button>
                      ))}
                      {items.map((file) => {
                        const Icon = fileIcon(file.type);
                        return (
                          <div className="project-files__item" key={file.id}>
                            <button
                              type="button"
                              className="project-files__open"
                              disabled={deleting === file.id}
                              onClick={() => open(file)}
                              title={file.name}>
                              <Icon size={22} />
                              <span>
                                <strong>{file.name}</strong>
                                <small>
                                  {deleting === file.id ? "Deleting…" : sizeLabel(file.size)}
                                  {file.folder ? ` · ${file.folder}` : ""}
                                </small>
                              </span>
                            </button>
                            {editable && (
                              <TinyContextMenu
                                items={[
                                  ...(id === "project"
                                    ? [
                                        {
                                          key: "folder",
                                          label: file.folder ? "Change folder" : "Add to folder",
                                          icon: <FiFolder />,
                                          disabled: busy || !!upload,
                                          onPress: () => {
                                            setMoving(file);
                                            setFolderName(file.folder || "");
                                          },
                                        },
                                      ]
                                    : []),
                                  {
                                    key: "delete",
                                    label: "Delete",
                                    icon: <FiTrash2 />,
                                    danger: true,
                                    disabled: busy || !!upload,
                                    onPress: () => remove(file),
                                  },
                                ]}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
      <UploadOverlay visible={!!upload} progress={upload?.progress} status={upload?.status} inline />
      {viewer && (
        <FileDialog title={viewer.file.name} onClose={closeViewer}>
          {viewer.loading ? (
            <p role="status">Opening file…</p>
          ) : viewer.error ? (
            <p role="alert">{viewer.error}</p>
          ) : (
            <>
              {viewer.previewFailed ? (
                <p>Preview unavailable. Use Open / download below.</p>
              ) : previewKind(viewer.file.type) === "image" ? (
                <img src={viewer.url} alt={viewer.file.name} />
              ) : previewKind(viewer.file.type) === "video" ? (
                <video controls src={viewer.url} />
              ) : previewKind(viewer.file.type) === "audio" ? (
                <audio controls src={viewer.url} />
              ) : previewKind(viewer.file.type) === "pdf" ? (
                <iframe title={viewer.file.name} src={viewer.url} />
              ) : (
                <p>No in-app preview for this file type.</p>
              )}
              <a href={viewer.downloadUrl || viewer.url} target="_blank" rel="noopener noreferrer">
                Open / download file
              </a>
            </>
          )}
        </FileDialog>
      )}
      {moving && (
        <FileDialog title="Move to folder" onClose={() => !busy && setMoving(null)}>
          <form onSubmit={saveFolder}>
            {error && <p role="alert">{error}</p>}
            <label>
              Folder name
              <input
                autoFocus
                list="file-folders"
                value={folderName}
                maxLength={80}
                onChange={(e) => setFolderName(e.target.value)}
              />
            </label>
            <datalist id="file-folders">
              {folders.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
            <p>Leave blank to remove the folder.</p>
            <button disabled={busy}>{busy ? "Moving…" : "Move"}</button>
          </form>
        </FileDialog>
      )}
      <Outlet />
    </div>
  );
}
function FileDialog({ title, onClose, children }) {
  const dialog = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const el = dialog.current;
    const prior = document.activeElement;
    el.showModal();
    return () => {
      el.close();
      prior?.focus?.();
    };
  }, []);
  return (
    <dialog
      ref={dialog}
      className="project-files__dialog"
      onCancel={(e) => {
        e.preventDefault();
        close.current();
      }}>
      <header>
        <strong>{title}</strong>
        <button type="button" aria-label="Close" onClick={onClose}>
          <FiX />
        </button>
      </header>
      {children}
    </dialog>
  );
}
